import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PhoneForwarded, Plus } from "lucide-react";

export const RedialBar = ({ autopilot, admin, reload, count, mlo }) => {
  const [busy, setBusy] = useState(false);
  const toggle = async (on) => {
    try { await api.post("/appointments/redial/autopilot", { on }); toast.success(on ? "Ava will redial missed appointments every day" : "Re-dial autopilot is off"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const now = async () => {
    if (!window.confirm(`Have Ava call and text all ${count} missed appointments now?`)) return;
    setBusy(true);
    try { const r = await api.post("/appointments/redial-now", null, { params: mlo ? { mlo_id: mlo } : {} }); toast.success(`Ava is calling and texting ${r.data.queued} missed appointments`); setTimeout(reload, 4000); }
    catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <Card className="mb-4 flex flex-wrap items-center justify-between gap-3 p-4" data-testid="redial-bar">
      <div className="max-w-xl text-sm text-slate-600"><b className="text-slate-900">Missed appointments: {count}</b> - Ava calls and texts each one, tells them their loan officer emailed their pricing options, and offers to connect them now or book a phone, office or in-home appointment. She keeps trying daily (9 AM-8 PM their time) until they book, connect, or say they aren't interested.{admin ? " Your switch covers every loan officer." : ""}</div>
      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm font-medium text-slate-800">
          <Switch checked={!!autopilot} onCheckedChange={toggle} data-testid="redial-autopilot-switch" />Re-dial all autopilot</label>
        <Button onClick={now} disabled={busy || !count} className="gold-cta border-0 font-bold" data-testid="redial-now-btn"><PhoneForwarded className="mr-2 h-4 w-4" />Re-dial all immediately</Button>
      </div>
    </Card>
  );
};

const blank = { name: "", phone: "", email: "", date: "", time: "", appointment_type: "phone", location: "", no_show: false, mlo_id: "" };

export const AddAppointment = ({ admin, reload }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(blank);
  const [mlos, setMlos] = useState([]);
  useEffect(() => { if (admin && open) api.get("/admin/users").then((r) => setMlos(r.data.filter((u) => u.role === "mlo" && u.account_status === "active"))).catch(() => {}); }, [admin, open]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = async () => {
    if (!f.date || !f.time) { toast.error("Enter the appointment date and time"); return; }
    try {
      await api.post("/appointments/manual", { ...f, appointment_at: new Date(`${f.date}T${f.time}`).toISOString(), mlo_id: f.mlo_id || null });
      toast.success("Appointment added"); setOpen(false); setF(blank); reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} data-testid="appt-add-btn"><Plus className="mr-1 h-4 w-4" />Add appointment</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="appt-add-dialog">
          <DialogHeader><DialogTitle>Add an appointment</DialogTitle></DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label>Lead name</Label><Input value={f.name} onChange={set("name")} data-testid="appt-add-name" /></div>
            <div><Label>Phone</Label><Input value={f.phone} onChange={set("phone")} data-testid="appt-add-phone" /></div>
            <div className="sm:col-span-2"><Label>Email (optional)</Label><Input value={f.email} onChange={set("email")} data-testid="appt-add-email" /></div>
            <div><Label>Date</Label><Input type="date" value={f.date} onChange={set("date")} data-testid="appt-add-date" /></div>
            <div><Label>Time</Label><Input type="time" value={f.time} onChange={set("time")} data-testid="appt-add-time" /></div>
            <div><Label>Type</Label><select value={f.appointment_type} onChange={set("appointment_type")} className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm" data-testid="appt-add-type">
              <option value="phone">Phone</option><option value="office">In person - office</option><option value="in_home">In-home visit</option></select></div>
            <div><Label>Address (office / home)</Label><Input value={f.location} onChange={set("location")} data-testid="appt-add-location" /></div>
            {admin && <div className="sm:col-span-2"><Label>Loan officer</Label><select value={f.mlo_id} onChange={set("mlo_id")} className="h-9 w-full rounded-md border border-slate-200 px-2 text-sm" data-testid="appt-add-mlo">
              <option value="">Pick a loan officer</option>{mlos.map((m) => <option key={m.id} value={m.id}>{`${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email}</option>)}</select></div>}
            <label className="flex items-center gap-2 text-sm sm:col-span-2"><Switch checked={f.no_show} onCheckedChange={set("no_show")} data-testid="appt-add-noshow" />The lead didn't show up</label>
          </div>
          <Button onClick={save} className="mt-2" data-testid="appt-add-save">Save appointment</Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

const COLS = [["called", "Called"], ["reached", "Reached"], ["rebooked", "Rebooked"], ["connected", "Connected"], ["not_interested", "Not interested"]];

export const RedialReport = ({ admin, mlo }) => {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/appointments/redial/report", { params: mlo ? { mlo_id: mlo } : {} }).then((r) => setRows(r.data.rows)).catch((e) => { setRows([]); toast.error(apiError(e)); });
  }, [mlo]);
  const totals = Object.fromEntries(COLS.map(([k]) => [k, (rows || []).reduce((s, r) => s + r[k], 0)]));
  return (
    <Card className="mt-6 p-4" data-testid="redial-report">
      <h2 className="text-base font-bold text-slate-900 md:text-lg">Re-dial results by week</h2>
      <p className="mb-3 text-xs text-slate-500">Missed appointments Ava called in the last 8 weeks. Reached = the lead picked up. Each lead counts once per week.</p>
      <div className="mb-3 flex flex-wrap gap-2" data-testid="redial-report-totals">
        {COLS.map(([k, l]) => <span key={k} className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700" data-testid={`redial-total-${k}`}>{l}: <b>{totals[k]}</b></span>)}
      </div>
      {rows === null ? <p className="text-sm text-slate-400">Loading…</p> : rows.length === 0 ? <p className="text-sm text-slate-400" data-testid="redial-report-empty">No re-dial calls yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="redial-report-table">
            <thead><tr className="text-left text-xs uppercase text-slate-500"><th className="py-2 pr-3">Week of</th>{admin && <th className="pr-3">Loan officer</th>}{COLS.map(([k, l]) => <th key={k} className="pr-3 text-right">{l}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={`${r.mlo_id}-${r.week_of}`} data-testid={`redial-report-row-${r.mlo_id}-${r.week_of}`}>
                  <td className="py-2 pr-3">{new Date(`${r.week_of}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</td>
                  {admin && <td className="pr-3">{r.mlo_name}</td>}
                  {COLS.map(([k]) => <td key={k} className="pr-3 text-right font-medium">{r[k]}</td>)}
                </tr>))}
            </tbody>
          </table>
        </div>)}
    </Card>
  );
};

export const NoShowToggle = ({ a, reload }) => {
  const on = a.appointment_status === "no-show";
  const set = async (v) => {
    try { await api.post(`/appointments/${a.id}/no-show-flag`, { no_show: v }); toast.success(v ? "Marked as a no-show" : "No-show removed"); reload(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="mt-2">
      <label className="flex items-center gap-2 text-xs font-medium text-slate-700"><Switch checked={on} onCheckedChange={set} data-testid={`appt-noshow-switch-${a.id}`} />No-show</label>
      {a.redial?.result && <p className="mt-1 text-xs text-slate-500" data-testid={`appt-redial-result-${a.id}`}><b>Re-dial:</b> {a.redial.result}{a.redial.count ? ` (${a.redial.count} day${a.redial.count > 1 ? "s" : ""})` : ""}</p>}
    </div>
  );
};
